import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { ShieldAlert, LogOut } from "lucide-react";

/**
 * Compact impersonation pill — designed to live INSIDE a role's existing
 * navigation bar (Customer / Shop / Franchise), not as a standalone top
 * banner. Renders nothing when not impersonating.
 *
 * Read-only mode is enforced server-side by `block_impersonation_writes`
 * middleware. This pill is just the visual signal + a one-click exit.
 *
 * Usage:
 *   <header className="…">
 *     <div className="flex items-center justify-between …">
 *       <… logo / left side …>
 *       <ImpersonationBanner />
 *       <… right side actions …>
 *     </div>
 *   </header>
 */
export default function ImpersonationBanner({ compact = false }) {
  const { impersonating, impersonatedName, userType, stopImpersonation } = useAuth();
  const navigate = useNavigate();

  if (!impersonating) return null;

  const handleExit = () => {
    stopImpersonation();
    navigate("/nedhaavu/dashboard");
  };

  const label =
    impersonatedName ||
    (userType === "shop"
      ? "shop"
      : userType === "franchise_owner"
      ? "franchise owner"
      : "customer");

  return (
    <div
      data-testid="impersonation-banner"
      className="inline-flex items-center gap-2 rounded-full bg-orange-500/15 border border-orange-500/50 text-orange-700 dark:text-orange-300 px-3 py-1"
      role="alert"
    >
      <ShieldAlert className="h-4 w-4 shrink-0" />
      <span className="text-xs font-medium whitespace-nowrap">
        <span className="font-bold">Read-only</span>
        <span className="hidden sm:inline">
          {" "}— viewing as {label}
        </span>
      </span>
      {!compact && (
        <Button
          size="sm"
          variant="ghost"
          onClick={handleExit}
          data-testid="exit-impersonation-btn"
          className="h-6 px-2 text-xs text-orange-700 dark:text-orange-300 hover:bg-orange-500/25 -mr-1"
        >
          <LogOut className="h-3 w-3 sm:mr-1" />
          <span className="hidden sm:inline">Exit</span>
        </Button>
      )}
    </div>
  );
}
